import { tokens } from "lib/colors"
import { GetBadgeData } from "@graphql/homepage/get_badge_data"
import { DrawerContext } from "context/DrawerContext"
import fetcher from "lib/fetcher"
import { checkStringContain } from "lib/helpers"
import { colorType } from "lib/interfaces"
import Button from "lib/components/Button"
import Dropdown, { IconColorType } from "lib/components/Dropdown"
import Text, { weightProps } from "lib/components/Text"
import { LanguageContext } from "context/LanguageContext"
import { useAuth } from "lib/auth"
import { gtmEvent } from "lib/gtm"
import Image from "next/legacy/image"
import Link from "next/link"
import { useRouter } from "next/router"
import { useCallback, useContext, useEffect, useState } from "react"
import useSWR from "swr"
import consola from "consola"
import Icon from "lib/components/Icons"
import Avatar from "lib/components/Avatar"
import { GetUser, UserDetail } from "@graphql/settings/get_user"
import LanguageOption from "@components/Selector/Language/Option"
import CategorySelectorBlog from "lib/components/CategorySelector/blog"
import { CategoryContext } from "context/CategoryContext"
import safeLocalStorage from "lib/storage"
import BlogDropdownLifeAt from "@components/Blog/Dropdown/LifeAt"
import BlogDropdownCollaboration from "@components/Blog/Dropdown/Collaboration"
import { useRoleTour } from "context/RoleTourProvider"
import UserDropdown from "./UserDropdown"
import JoinUsModal from "./joinModal"
import { AlertUserVerification } from "."

export const routeHomepage = [
  "/login",
  "/register",
  "/business",
  "/become-trainer",
  "/referral",
  "/about",
  "/programs",
  "/trusted",
  "/certifications",
  "/terms",
  "/trainer-form",
  "/provider-form",
  "/forgot-password",
]

interface HeaderProps {
  theme?: "default" | "light" | "transparent"
  withoutBorded?: boolean
  withoutLogo?: boolean
  isFixed?: boolean
  handleLogout?: () => void
  isAdmin?: boolean
  isAbsolute?: boolean
}

interface BadgeProfileProps {
  textColor: colorType
  firstName: string
  lastName: string
  userDetail: UserDetail[]
}

interface BulletRedProps {
  count: number
  withNumberCount?: boolean
}

declare const window: {
  dispatchEvent(storageEvent: Event): unknown
  QismoApp
  location
  addEventListener
  removeEventListener
  scrollY
}

const activeTab = [
  { value: "latest", label: "Latest" },
  { value: "insight", label: "Insight" },
  { value: "trainer", label: "Trainer Story" },
  { value: "client", label: "Client Story" },
  { value: "life", label: "Life at PasarTrainer" },
]

function BulletRed({ count = 0, withNumberCount = false }: BulletRedProps) {
  if (count > 0) {
    if (withNumberCount) {
      return (
        <Text
          alignText="center"
          size="extra-small"
          weight="bold"
          color="light"
          customClass="bg-danger w-6 h-6 rounded-full flex items-center justify-center">
          {count}
        </Text>
      )
    }
    return (
      <div className="inline-flex absolute -top-0 -right-1 justify-center items-center w-2.5 h-2.5 text-xs font-bold text-white bg-danger rounded-full" />
    )
  }

  return <div className="hidden" />
}

function BadgeProfile({ firstName, lastName, textColor, userDetail }: BadgeProfileProps) {
  let bgBadge = "bg-primary-pressed"
  let sliceName = `${firstName?.slice(0, 1)}${lastName?.slice(0, 1)}`

  if (lastName === "." || lastName === "") {
    sliceName = firstName?.slice(0, 2)
  }

  if (textColor === "primary") {
    bgBadge = "bg-primary-border"
  }

  if (userDetail.length > 0) {
    if (userDetail[0].profile_picture !== null) {
      return (
        <Avatar
          rounded="full"
          variant="circular-image"
          url={userDetail[0].profile_picture}
          size="small"
          customSize="w-10 h-10"
        />
      )
    }

    return (
      <div className={`flex justify-center items-center rounded-full w-10 h-10 ${bgBadge}`}>
        <Text color={textColor} weight="medium" alignText="center" customClass="uppercase">
          {sliceName}
        </Text>
      </div>
    )
  }

  return (
    <div className={`flex justify-center items-center rounded-full w-10 h-10 ${bgBadge}`}>
      <Text color={textColor} weight="medium" alignText="center" customClass="uppercase">
        {sliceName}
      </Text>
    </div>
  )
}
function Header({
  theme,
  withoutBorded,
  withoutLogo,
  isFixed = false,
  handleLogout,
  isAdmin = false,
  isAbsolute = false,
}: HeaderProps) {
  const router = useRouter()
  const { userProfile, isLoggedIn } = useAuth()
  const { forceUserMenuOpen } = useRoleTour()
  const { toggleDrawer } = useContext(DrawerContext)
  const { useTranslation: t, language } = useContext(LanguageContext)
  const [badgeDataUrl, setBadgeDataUrl] = useState(null)
  const [showModalJoin, setShowModalJoin] = useState(false)
  const [newMessages, setNewMessages] = useState([])
  const { data: dataBadge } = useSWR<GetBadgeData>(badgeDataUrl, fetcher)
  const [activeTheme, setActiveTheme] = useState(theme)
  const [searchBlogValue, setSearchBlogValue] = useState<string>("")
  const [openSearch, setOpenSearch] = useState<boolean>(false)
  const [selectedTab, setSelectedTab] = useState<string>(router.query?.type?.toString() || "latest")
  const [activeDropdown, setActiveDropdown] = useState(null)

  const [userUrl, setUserUrl] = useState<string | null>(null)

  const { data: dataUsers } = useSWR<{ data: GetUser }>(userUrl, fetcher)
  const { categories: dataCategory } = useContext(CategoryContext)

  const categories = dataCategory?.data?.program_category || []

  useEffect(() => {
    if (userProfile?.session_id) {
      setBadgeDataUrl("/api/homepage/get-badge-data")
      setUserUrl("/api/settings/get-user")
    }

    return () => {
      setBadgeDataUrl(null)
      setUserUrl(null)
    }
  }, [userProfile])

  useEffect(() => {
    const handleStorage = (e) => {
      try {
        if (e.target.localStorage.newMessage) {
          setNewMessages(e.target.localStorage.newMessage ? JSON.parse(e.target.localStorage.newMessage) : [])
        } else {
          setNewMessages([])
        }
      } catch (error) {
        consola.error(error)
      }
    }

    window.addEventListener("storage", handleStorage)
    return () => window.removeEventListener("storage", handleStorage)
  }, [])

  useEffect(() => {
    setSearchBlogValue(router.query.q as string)
    setSelectedTab(router.query.type?.toString() || "latest")

    return () => {
      setSearchBlogValue("")
    }
  }, [router.query])

  const changeTheme = useCallback(() => {
    if (router.pathname === "/") {
      const { scrollY } = window
      if (scrollY > 600) {
        setActiveTheme("light")
      } else {
        setActiveTheme("default")
      }
    }
  }, [router.pathname])

  useEffect(() => {
    window.addEventListener("scroll", changeTheme)
    return () => window.removeEventListener("scroll", changeTheme)
  }, [changeTheme])

  const routeDefault = ["/login", "/register"]

  if (router.pathname !== "/" && !checkStringContain(router.asPath, routeDefault)) {
    theme = "light"
  }

  let backgroundColor = "bg-primary"
  let iconColor = "text-white"
  let textColor: colorType = "light"
  let weight: weightProps = "normal"
  let dropDownIconColor: IconColorType = "white"
  let assetLogo = "/assets/images/logo-large-secondary.png"
  let marginXHeader = "px-4 lg:px-0 lg:mx-auto"
  let headerRightMargin = "pr-0"

  if (theme === "light" || activeTheme === "light") {
    backgroundColor = `bg-white ${withoutBorded ? "" : "shadow-bottom"}`
    assetLogo = "/assets/images/logo-large-primary.png"
    iconColor = "text-default"
    textColor = "primary"
    dropDownIconColor = "primary"
    weight = "medium"
    marginXHeader = "px-4 xl:px-0 2xl:px-0"
  }

  if (theme === "transparent" || activeTheme === "transparent") {
    backgroundColor = `bg-transparent ${withoutBorded ? "" : "shadow-bottom"}`
    assetLogo = "/assets/images/logo-large-secondary.png"
    iconColor = "text-default"
    textColor = "light"
    dropDownIconColor = "white"
    weight = "medium"
    marginXHeader = "px-4 xl:px-0 2xl:px-0"
  }

  if (isAdmin) {
    headerRightMargin = "pr-0"
  }

  const searchBlogList = () => {
    if (searchBlogValue !== "") {
      return router.push(`/blog?q=${searchBlogValue}&type=insight`)
    }
    return router.push("/blog")
  }

  return (
    <>
      <div
        className={`w-full z-20 ${backgroundColor} h-[52px] lg:h-20 ${isFixed ? "fixed" : ""} ${
          isAbsolute ? "absolute" : ""
        }
        `}>
        <div
          className={`relative ${marginXHeader} ${
            !isAdmin ? "lg:container" : ""
          } flex gap-4 sm:gap-8 h-full items-center justify-between mx-auto ${headerRightMargin}`}>
          <div className={`${iconColor} w-10 relative right-0.5 lg:hidden`}>
            {!openSearch ? (
              <Icon onClick={toggleDrawer} iconName="menu" color={tokens.primary.DEFAULT} />
            ) : (
              <Icon
                onClick={() => setOpenSearch(false)}
                iconName="arrowLeft"
                color={tokens.primary.DEFAULT}
                width={32}
                height={32}
              />
            )}
          </div>
          <div
            className={`${openSearch ? "hidden" : "flex"} gap-0 ${
              !isAdmin ? "lg:gap-x-10" : "lg:gap-x-8"
            } items-center`}>
            <div className="cursor-pointer h-full">
              <div
                tabIndex={0}
                role="button"
                className={`${withoutLogo ? "block lg:hidden h-inherit" : "block h-inherit"}`}
                onClick={() => {
                  safeLocalStorage.setItem("active_route", "home")
                  router.push("/")
                }}>
                <Link href="/" legacyBehavior>
                  <a className="flex items-center h-inherit">
                    <Image src={assetLogo} alt="Picture of the author" width={148} height={33} priority />
                  </a>
                </Link>
              </div>
            </div>
          </div>
          <div
            className={`${
              openSearch ? "flex w-full" : "w-[35rem] hidden lg:flex"
            } items-center rounded-lg bg-[#F7F9FF] py-2.5 px-3 h-10`}>
            <Icon iconName="search-light" styles="w-5 h-5" color={tokens.neutral[50]} />
            <input
              name="search"
              type="text"
              placeholder={t("search_for_topics_or_keywords")}
              value={searchBlogValue || ""}
              className="pl-2.5 w-full h-full rounded-lg text-sm font-normal text-black bg-[#F7F9FF]"
              onChange={(event) => setSearchBlogValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  searchBlogList()
                }
              }}
            />
          </div>
          {!openSearch ? (
            <div className="w-10 lg:hidden">
              <Icon
                iconName="search"
                width={30}
                height={30}
                color={tokens.primary.DEFAULT}
                onClick={() => setOpenSearch(true)}
              />
            </div>
          ) : (
            <div className="w-10 lg:hidden">
              <Icon
                iconName="close"
                width={30}
                height={30}
                color={tokens.neutral[50]}
                onClick={() => setOpenSearch(false)}
              />
            </div>
          )}
          <div className="hidden lg:flex flex-row items-center gap-x-8">
            <div className="flex flex-row items-center gap-3">
              <div
                role="button"
                tabIndex={0}
                onClick={() => gtmEvent("ce_follow_social", { social_media: "Instagram" })}>
                <Link href="https://www.instagram.com/pasartrainer/" legacyBehavior>
                  <a className="block" aria-label="Instagram">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_2065_731)">
                        <path
                          d="M8.67857 8.02232C8.40922 8.19155 8.17654 8.41307 7.99428 8.67378C7.81202 8.93448 7.68389 9.22909 7.61745 9.54018C7.55102 9.85126 7.54763 10.1725 7.6075 10.4849C7.66736 10.7973 7.78926 11.0946 7.96599 11.3591C8.14271 11.6236 8.37068 11.8499 8.6364 12.0248C8.90212 12.1997 9.20021 12.3195 9.51304 12.3772C9.82587 12.4348 10.1471 12.4292 10.4577 12.3606C10.7683 12.292 11.062 12.1618 11.3214 11.9777C11.5908 11.8084 11.8235 11.5869 12.0057 11.3262C12.188 11.0655 12.3161 10.7709 12.3825 10.4598C12.449 10.1487 12.4524 9.82749 12.3925 9.51508C12.3326 9.20266 12.2107 8.90542 12.034 8.64093C11.8573 8.37644 11.6293 8.15007 11.3636 7.9752C11.0979 7.80034 10.7998 7.68052 10.487 7.62285C10.1741 7.56518 9.85292 7.57081 9.54231 7.63943C9.2317 7.70804 8.938 7.83824 8.67857 8.02232ZM15.0313 4.96875C14.7991 4.73661 14.5179 4.55357 14.2098 4.43304C13.4018 4.11607 11.6384 4.12946 10.5 4.14286C10.317 4.14286 10.1473 4.14732 10 4.14732C9.85268 4.14732 9.67857 4.14732 9.49107 4.14286C8.35268 4.12946 6.59821 4.11161 5.79018 4.43304C5.48214 4.55357 5.20536 4.73661 4.96875 4.96875C4.73214 5.20089 4.55357 5.48214 4.43304 5.79018C4.11607 6.59821 4.13393 8.36607 4.14286 9.50446C4.14286 9.6875 4.14732 9.85714 4.14732 10C4.14732 10.1429 4.14732 10.3125 4.14286 10.4955C4.13393 11.6339 4.11607 13.4018 4.43304 14.2098C4.55357 14.5179 4.73661 14.7946 4.96875 15.0313C5.20089 15.2679 5.48214 15.4464 5.79018 15.567C6.59821 15.8839 8.36161 15.8705 9.5 15.8571C9.68304 15.8571 9.85268 15.8527 10 15.8527C10.1473 15.8527 10.3214 15.8527 10.5089 15.8571C11.6473 15.8705 13.4018 15.8884 14.2098 15.567C14.5179 15.4464 14.7946 15.2634 15.0313 15.0313C15.2679 14.7991 15.4464 14.5179 15.567 14.2098C15.8884 13.4062 15.8705 11.6473 15.8571 10.5045C15.8571 10.317 15.8527 10.1429 15.8527 9.99554C15.8527 9.84821 15.8527 9.67857 15.8571 9.48661C15.8705 8.34821 15.8884 6.58929 15.567 5.78125C15.4464 5.47321 15.2634 5.19643 15.0313 4.95982V4.96875ZM12.0357 6.95536C12.8438 7.49554 13.4018 8.33482 13.5938 9.28571C13.7857 10.2366 13.5848 11.2277 13.0446 12.0357C12.7768 12.4375 12.433 12.7768 12.0357 13.0446C11.6384 13.3125 11.1875 13.4955 10.7143 13.5893C9.76339 13.7768 8.77232 13.5804 7.96429 13.0402C7.15625 12.5 6.59821 11.6607 6.40625 10.7098C6.21429 9.75893 6.41518 8.76786 6.95089 7.95982C7.48661 7.15179 8.33036 6.59375 9.28125 6.40179C10.2321 6.20982 11.2232 6.41071 12.0312 6.94643H12.0357V6.95536ZM13.3393 6.89732C13.2009 6.80357 13.0893 6.66964 13.0223 6.51339C12.9554 6.35714 12.942 6.1875 12.9732 6.01786C13.0045 5.84821 13.0893 5.70089 13.2054 5.58036C13.3214 5.45982 13.4777 5.37946 13.6429 5.34821C13.808 5.31696 13.9821 5.33036 14.1384 5.39732C14.2946 5.46429 14.4286 5.57143 14.5223 5.70982C14.6161 5.84821 14.6652 6.01339 14.6652 6.18304C14.6652 6.29464 14.6429 6.40625 14.6027 6.50893C14.5625 6.61161 14.4955 6.70536 14.4196 6.78571C14.3438 6.86607 14.2455 6.92857 14.1429 6.97321C14.0402 7.01786 13.9286 7.04018 13.817 7.04018C13.6473 7.04018 13.4821 6.99107 13.3438 6.89732H13.3393ZM20 2.85714C20 1.28125 18.7188 0 17.1429 0H2.85714C1.28125 0 0 1.28125 0 2.85714V17.1429C0 18.7188 1.28125 20 2.85714 20H17.1429C18.7188 20 20 18.7188 20 17.1429V2.85714ZM15.9375 15.9375C15.1027 16.7723 14.0893 17.0357 12.9464 17.0938C11.7679 17.1607 8.23214 17.1607 7.05357 17.0938C5.91071 17.0357 4.89732 16.7723 4.0625 15.9375C3.22768 15.1027 2.96429 14.0893 2.91071 12.9464C2.84375 11.7679 2.84375 8.23214 2.91071 7.05357C2.96875 5.91071 3.22768 4.89732 4.0625 4.0625C4.89732 3.22768 5.91518 2.96429 7.05357 2.91071C8.23214 2.84375 11.7679 2.84375 12.9464 2.91071C14.0893 2.96875 15.1027 3.22768 15.9375 4.0625C16.7723 4.89732 17.0357 5.91071 17.0893 7.05357C17.1562 8.22768 17.1562 11.7589 17.0893 12.942C17.0312 14.0848 16.7723 15.0982 15.9375 15.933V15.9375Z"
                          fill={tokens.neutral[70]}
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_2065_731">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                </Link>
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => gtmEvent("ce_follow_social", { social_media: "Linkedin" })}>
                <Link href="https://www.linkedin.com/company/pasartrainer/" legacyBehavior>
                  <a className="block" aria-label="Linkedin">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_2065_733)">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M2.85714 0C2.09938 0 1.37266 0.30102 0.836838 0.836838C0.30102 1.37266 0 2.09938 0 2.85714V17.1429C0 17.9006 0.30102 18.6273 0.836838 19.1632C1.37266 19.699 2.09938 20 2.85714 20H17.1429C17.9006 20 18.6273 19.699 19.1632 19.1632C19.699 18.6273 20 17.9006 20 17.1429V2.85714C20 2.09938 19.699 1.37266 19.1632 0.836838C18.6273 0.30102 17.9006 0 17.1429 0H2.85714ZM4.43143 6.13857C4.88419 6.13857 5.31841 5.95871 5.63856 5.63856C5.95871 5.31841 6.13857 4.88419 6.13857 4.43143C6.13857 3.97867 5.95871 3.54445 5.63856 3.2243C5.31841 2.90415 4.88419 2.72429 4.43143 2.72429C3.97867 2.72429 3.54445 2.90415 3.2243 3.2243C2.90415 3.54445 2.72429 3.97867 2.72429 4.43143C2.72429 4.88419 2.90415 5.31841 3.2243 5.63856C3.54445 5.95871 3.97867 6.13857 4.43143 6.13857ZM5.86 16.8757V7.47714H3.00286V16.8757H5.86ZM7.75714 7.47714H10.6143V8.73571C11.0357 8.07571 11.9614 7.18714 13.6829 7.18714C15.7371 7.18714 16.8529 8.54857 16.8529 11.1386C16.8529 11.2629 16.8643 11.83 16.8643 11.83V16.8743H14.0071V11.8314C14.0071 11.1386 13.8614 9.77714 12.3214 9.77714C10.78 9.77714 10.65 11.4886 10.6143 12.6086V16.8743H7.75714V7.47714Z"
                          fill={tokens.neutral[70]}
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_2065_733">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                </Link>
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => gtmEvent("ce_follow_social", { social_media: "Facebook" })}>
                <Link href="https://www.facebook.com/PasarTrainer/" legacyBehavior>
                  <a className="block" aria-label="Facebook">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_2065_735)">
                        <path
                          d="M17.7778 0H2.22222C1 0 0 1 0 2.22222V17.7778C0 19.0011 1 20 2.22222 20H10V12.2222H7.77778V9.47222H10V7.19444C10 4.79 11.3467 3.10111 14.1844 3.10111L16.1878 3.10333V5.99778H14.8578C13.7533 5.99778 13.3333 6.82667 13.3333 7.59556V9.47333H16.1867L15.5556 12.2222H13.3333V20H17.7778C19 20 20 19.0011 20 17.7778V2.22222C20 1 19 0 17.7778 0Z"
                          fill={tokens.neutral[70]}
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_2065_735">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                </Link>
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => gtmEvent("ce_follow_social", { social_media: "Twitter" })}>
                <Link href="https://twitter.com/pasartrainer" legacyBehavior>
                  <a className="block" aria-label="Twitter">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_2065_737)">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3.63636 0C2.67194 0 1.74702 0.383116 1.06507 1.06507C0.383116 1.74702 0 2.67194 0 3.63636V16.3636C0 17.3281 0.383116 18.253 1.06507 18.9349C1.74702 19.6169 2.67194 20 3.63636 20H16.3636C17.3281 20 18.253 19.6169 18.9349 18.9349C19.6169 18.253 20 17.3281 20 16.3636V3.63636C20 2.67194 19.6169 1.74702 18.9349 1.06507C18.253 0.383116 17.3281 0 16.3636 0H3.63636ZM3.33273 3.18182C3.23321 3.2188 3.14371 3.27851 3.07134 3.3562C2.99898 3.43388 2.94575 3.52739 2.91591 3.62928C2.88607 3.73117 2.88043 3.83861 2.89945 3.94306C2.91847 4.04751 2.96162 4.14608 3.02545 4.23091L8.12909 11.0036L2.75182 16.7718L2.71182 16.8182H4.57273L8.96364 12.11L12.3382 16.59C12.4165 16.6937 12.5228 16.7729 12.6445 16.8182H16.6645C16.7639 16.781 16.8532 16.7211 16.9254 16.6434C16.9976 16.5656 17.0506 16.4721 17.0802 16.3702C17.1099 16.2683 17.1154 16.161 17.0962 16.0566C17.0771 15.9522 17.0339 15.8538 16.97 15.7691L11.8664 8.99636L17.2882 3.18182H15.4245L11.0336 7.89091L7.65727 3.41091C7.57911 3.30687 7.4728 3.22738 7.35091 3.18182H3.33273ZM13.2236 15.4982L4.93727 4.50182H6.77273L15.0582 15.4973L13.2236 15.4982Z"
                          fill={tokens.neutral[70]}
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_2065_737">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                </Link>
              </div>
            </div>
            <div className="flex flex-row items-center gap-5">
              <LanguageOption colorTitleDropdown={textColor} />
              {isLoggedIn && userProfile && (
                <Dropdown
                  showIconExpand
                  colorButton="primary"
                  customGapButton="!gap-1"
                  withoutBackground
                  fullWidth
                  iconColor={dropDownIconColor}
                  pathName={router.pathname}
                  forceOpen={forceUserMenuOpen}
                  title={
                    <div className="relative">
                      <BadgeProfile
                        textColor={textColor}
                        firstName={userProfile.first_name}
                        lastName={userProfile.last_name}
                        userDetail={dataUsers?.data?.user_user?.[0]?.user_detail || []}
                      />

                      <BulletRed
                        count={
                          (dataBadge?.data?.order_count?.aggregate?.count || 0) +
                          (newMessages?.length || 0)
                        }
                      />
                    </div>
                  }>
                  <UserDropdown
                    userProfile={userProfile}
                    orderCount={dataBadge?.data?.order_count?.aggregate?.count || 0}
                    handleLogout={handleLogout}
                  />
                </Dropdown>
              )}
              {!isLoggedIn && !userProfile && (
                <div className="grid grid-cols-2 gap-5">
                  <Button
                    variant="outlined"
                    color={theme !== "light" && activeTheme !== "light" ? "white" : "primary"}
                    customClass="min-w-[84px]"
                    onClick={() => {
                      gtmEvent("ce_header_menu_click", { menu_name: "Login" })
                      if (
                        router.asPath !== "/" &&
                        !checkStringContain(router.asPath, routeHomepage)
                      ) {
                        router.push(`/login?r=${encodeURIComponent(router.asPath)}`)
                      } else {
                        router.push("/login")
                      }
                    }}>
                    {t("login")}
                  </Button>
                  <Button
                    variant="contained"
                    color={theme !== "light" && activeTheme !== "light" ? "white" : "primary"}
                    customClass="min-w-[84px]"
                    onClick={() => {
                      setShowModalJoin(true)
                      gtmEvent("ce_header_menu_click", { menu_name: "Sign Up" })
                    }}>
                    {t("register")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="hidden lg:block shadow-md bg-white w-full h-14 lg:h-[52px] relative">
          <div className="h-full container mx-auto px-4 lg:px-0 flex flex-row items-center gap-x-4 lg:gap-x-8">
            <div
              tabIndex={0}
              role="button"
              className={`flex-shrink-0 ${
                selectedTab === "latest"
                  ? "border-b-2 border-primary"
                  : "border-b-2 border-transparent"
              } py-1.5 lg:py-3 lg:my-0 cursor-pointer`}
              onClick={() => {
                setSelectedTab("latest")
                router.push("/blog?type=latest")
              }}>
              <Text
                customClass={`font-semibold ${
                  selectedTab === "latest" ? "!text-primary" : "!text-neutral-50"
                } text-sm lg:text-base`}>
                {t("latest")}
              </Text>
            </div>
            <CategorySelectorBlog
              language={language}
              title="Insight"
              allCategory={t("all_categories")}
              topCategories={t("top_categories")}
              color={textColor}
              weight="medium"
              isHeader={router.query.type?.toString() === "insight"}
              categories={categories}
              setActiveDropdown={setActiveDropdown}
              activeDropdown={activeDropdown}
            />
            <div
              tabIndex={0}
              role="button"
              className={`flex-shrink-0 ${
                selectedTab === "trainer"
                  ? "border-b-2 border-primary"
                  : "border-b-2 border-transparent"
              } py-1.5 lg:py-3 lg:my-0 cursor-pointer`}
              onClick={() => {
                setSelectedTab("trainer")
                router.push("/blog?type=trainer")
              }}>
              <Text
                customClass={`font-semibold ${
                  selectedTab === "trainer" ? "!text-primary" : "!text-neutral-50"
                } text-sm lg:text-base`}>
                {t("trainer_story")}
              </Text>
            </div>
            <BlogDropdownLifeAt 
              language={language}
              title="Life at PasarTrainer"
              allCategory={t("all_categories")}
              topCategories={t("top_categories")}
              color={textColor}
              weight="medium"
              isHeader={router.query.type?.toString() === "life-at-pasartrainer"}
              categories={categories}
              setActiveDropdown={setActiveDropdown}
              activeDropdown={activeDropdown}
            />
            <BlogDropdownCollaboration 
              language={language}
              title="Collaboration"
              allCategory={t("all_categories")}
              topCategories={t("top_categories")}
              color={textColor}
              weight="medium"
              isHeader={router.query.type?.toString() === "collaboration"}
              categories={categories}
              setActiveDropdown={setActiveDropdown}
              activeDropdown={activeDropdown}
            />
          </div>
        </div>
        {showModalJoin && (
          <JoinUsModal
            onClose={() => {
              setShowModalJoin(false)
            }}
          />
        )}

        {isLoggedIn && userProfile && !userProfile?.is_verified && <AlertUserVerification />}
      </div>
      {isLoggedIn && userProfile && !userProfile?.is_verified && (
        <div className="h-24 sm:h-16 lg:h-6 xl:h-6 2xl:h-8" />
      )}
    </>
  )
}

Header.defaultProps = {
  theme: "default",
  withoutBorded: false,
  withoutLogo: false,
}

export default Header
